import { useEffect, useRef, useCallback } from 'react';
import { usePlayerStore } from '../store/playerStore';
import { useAuth } from '../context/AuthContext';
import { useFreeTrial } from '../context/FreeTrialContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Heartbeat interval in milliseconds
const HEARTBEAT_INTERVAL = 10000; // 10 seconds

// Internal debug logger — no-op in production builds
const _dev = process.env.NODE_ENV === 'development';
const dbg = _dev ? (...args) => console.log(...args) : () => {};

/**
 * MediaEngine - The Single Source of Truth for Media Playback
 * 
 * This component contains the ONLY <audio> and <video> elements in the entire app.
 * It is placed at the root level (App.js) so it never unmounts during navigation.
 * All player UIs (FloatingPlayer, EpisodePlayer) are just "remotes" that control this engine.
 * 
 * PLAYBACK SESSION:
 * Only one device can play at a time per account. When playback starts,
 * a session is created on the server. Heartbeats keep it alive.
 */
export function MediaEngine() {
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const lastTrackId = useRef(null);
  const lastTrackedTime = useRef(0);
  const heartbeatInterval = useRef(null);
  const historyRecordedRef = useRef(null); // Track which episode was recorded
  const trialPausedRef = useRef(false);    // Prevent multiple paywall triggers per track
  
  const { accessToken, isAuthenticated } = useAuth();
  const { freeTrial, updateProgress } = useFreeTrial();
  const lastProgressReportRef = useRef(0); // timestamp of last free-trial progress report
  
  // Get store state and actions
  const currentTrack = usePlayerStore(state => state.currentTrack);
  const isPlaying = usePlayerStore(state => state.isPlaying);
  const volume = usePlayerStore(state => state.volume);
  const isMuted = usePlayerStore(state => state.isMuted);
  const _seekTo = usePlayerStore(state => state._seekTo);
  const duration = usePlayerStore(state => state.duration);
  const sessionToken = usePlayerStore(state => state.sessionToken);
  const _forceStop = usePlayerStore(state => state._forceStop);
  
  const setIsPlaying = usePlayerStore(state => state.setIsPlaying);
  const setCurrentTime = usePlayerStore(state => state.setCurrentTime);
  const setDuration = usePlayerStore(state => state.setDuration);
  const setMediaError = usePlayerStore(state => state.setMediaError);
  const clearSeekCommand = usePlayerStore(state => state.clearSeekCommand);
  const clearForceStop = usePlayerStore(state => state.clearForceStop);
  const playNext = usePlayerStore(state => state.playNext);
  const setSessionToken = usePlayerStore(state => state.setSessionToken);
  const setSessionInvalid = usePlayerStore(state => state.setSessionInvalid);
  const setTrialExpiredPaywall = usePlayerStore(state => state.setTrialExpiredPaywall);
  
  const isVideo = currentTrack?.media_type === 'video';
  const isVideoInPage = usePlayerStore(state => state.isVideoInPage);
  
  // Get the active media ref
  const getActiveRef = useCallback(() => {
    return isVideo ? videoRef : audioRef;
  }, [isVideo]);
  
  // Track analytics events
  const trackEvent = useCallback(async (eventName, extraData = {}) => {
    if (!currentTrack || !accessToken) return;
    try {
      await axios.post(`${API}/events`, {
        event_name: eventName,
        episode_id: currentTrack.id,
        creator_id: currentTrack.creator_id,
        ...extraData,
      }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
    } catch (e) {
      console.error('Failed to track event:', eventName);
    }
  }, [currentTrack, accessToken]);
  
  // Record to history when audio starts playing
  const recordHistory = useCallback(async (episodeId) => {
    if (!accessToken || historyRecordedRef.current === episodeId) return;
    
    try {
      await axios.post(`${API}/episodes/${episodeId}/history`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      historyRecordedRef.current = episodeId;
    } catch (error) {
      console.error('Failed to record history:', error);
    }
  }, [accessToken]);
  
  // ========== PLAYBACK SESSION MANAGEMENT ==========
  
  // Start a new playback session
  const startPlaybackSession = useCallback(async (episodeId) => {
    if (!accessToken || !isAuthenticated) return null;
    
    try {
      const response = await axios.post(`${API}/playback/start`, {
        episode_id: episodeId,
        device_id: navigator.userAgent.substring(0, 50)
      }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      const token = response.data.session_token;
      setSessionToken(token);
      return token;
    } catch (e) {
      console.error('Failed to start playback session:', e);
      return null;
    }
  }, [accessToken, isAuthenticated, setSessionToken]);
  
  // Send heartbeat to keep session alive
  const sendHeartbeat = useCallback(async () => {
    if (!sessionToken || !accessToken) return;
    
    try {
      const ref = getActiveRef();
      const currentTime = ref.current?.currentTime || 0;
      
      const response = await axios.post(`${API}/playback/heartbeat`, {
        session_token: sessionToken,
        current_time: currentTime
      }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      
      if (!response.data.valid) {
        // Session was invalidated (another device started playing)
        setSessionInvalid(response.data.message || 'Přehrávání zastaveno - aktivní na jiném zařízení');
        
        // Stop playback
        const activeRef = getActiveRef();
        if (activeRef.current) {
          activeRef.current.pause();
        }
      }
    } catch (e) {
      console.error('Heartbeat failed:', e);
    }
  }, [sessionToken, accessToken, getActiveRef, setSessionInvalid]);
  
  // Stop playback session
  const stopPlaybackSession = useCallback(async () => {
    if (!sessionToken || !accessToken) return;
    
    try {
      await axios.post(`${API}/playback/stop`, {
        session_token: sessionToken
      }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
    } catch (e) {
      console.error('Failed to stop playback session:', e);
    }
  }, [sessionToken, accessToken]);
  
  // Start/stop heartbeat interval based on playback state
  useEffect(() => {
    if (isPlaying && sessionToken && isAuthenticated) {
      // Start heartbeat
      heartbeatInterval.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
      // Send immediate heartbeat
      sendHeartbeat();
    } else {
      // Stop heartbeat
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = null;
      }
    }
    
    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [isPlaying, sessionToken, isAuthenticated, sendHeartbeat]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPlaybackSession();
    };
  }, [stopPlaybackSession]);
  
  // ========== HANDLE FORCE STOP ==========
  // This effect responds immediately to force stop requests (e.g., when VideoPlayer mounts)
  useEffect(() => {
    if (_forceStop) {
      dbg('[MediaEngine] Force stop triggered - stopping all media');
      
      // Immediately pause and clear both elements
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
        audioRef.current.load();
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      }
      
      lastTrackId.current = null;
      stopPlaybackSession();
      clearForceStop();
    }
  }, [_forceStop, stopPlaybackSession, clearForceStop]);
  
  // ========== HANDLE TRACK CHANGES ==========
  useEffect(() => {
    if (!currentTrack) {
      // No track - pause both elements, clear src and stop session
      dbg('[MediaEngine] Track cleared - pausing all elements');
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
        audioRef.current.load();
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      }
      lastTrackId.current = null;
      stopPlaybackSession();
      return;
    }
    
    // If same track, skip reloading
    if (lastTrackId.current === currentTrack.id) return;
    
    dbg('[MediaEngine] New track detected:', currentTrack.title, currentTrack.media_type, '- loading media');
    lastTrackId.current = currentTrack.id;
    lastTrackedTime.current = 0;
    lastProgressReportRef.current = 0;
    trialPausedRef.current = false; // Reset free-trial gate for new track
    
    // Start new playback session for authenticated users
    if (isAuthenticated) {
      startPlaybackSession(currentTrack.id);
    }
    
    // Determine which element to use
    const activeRef = isVideo ? videoRef : audioRef;
    const inactiveRef = isVideo ? audioRef : videoRef;
    
    // Pause and clear the inactive element
    if (inactiveRef.current) {
      inactiveRef.current.pause();
      inactiveRef.current.removeAttribute('src');
      inactiveRef.current.load();
    }
    
    // Load the active element (but don't auto-play video - it's controlled by isPlaying state)
    if (activeRef.current && currentTrack.media_url) {
      dbg('[MediaEngine] Loading media URL:', currentTrack.media_url.substring(0, 100));
      
      // Set type attribute for video
      if (isVideo) {
        activeRef.current.setAttribute('type', 'video/mp4');
      }
      
      activeRef.current.src = currentTrack.media_url;
      activeRef.current.load();

      // resumeFrom: seek to saved position before playing so the video never
      // plays from 0 and then jumps — which would cause handleTimeUpdate to
      // write 0 into the store and the progress bar to flicker.
      //
      // keepPlaying: if the user was watching when they navigated away, start
      // playing automatically AFTER the seek completes (not before).
      //
      // isPlaying: used for new tracks with no resumeFrom (normal play).
      const resumeFrom   = currentTrack.resumeFrom  || 0;
      const keepPlaying  = currentTrack.keepPlaying  || false;

      const startPlay = () => {
        if (!activeRef.current) return;
        activeRef.current.play().catch(err => {
          dbg('[MediaEngine] play() prevented:', err);
        });
        setIsPlaying(true);
      };

      if (resumeFrom > 0) {
        // Seek first, then play (if requested) — prevents 0-position flicker.
        const doSeekThenPlay = () => {
          if (!activeRef.current) return;
          activeRef.current.currentTime = resumeFrom;
          if (keepPlaying) {
            // oncanplaythrough / onseeked is more reliable than calling play() right here
            const onSeeked = () => {
              activeRef.current?.removeEventListener('seeked', onSeeked);
              startPlay();
            };
            activeRef.current.addEventListener('seeked', onSeeked);
          }
        };

        if (activeRef.current.readyState >= 1) {
          doSeekThenPlay();
        } else {
          const onMetadata = () => {
            activeRef.current?.removeEventListener('loadedmetadata', onMetadata);
            doSeekThenPlay();
          };
          activeRef.current.addEventListener('loadedmetadata', onMetadata);
        }
      } else if (isPlaying) {
        // No seek needed — play as soon as the browser allows it.
        const handleCanPlay = () => {
          activeRef.current?.removeEventListener('canplay', handleCanPlay);
          activeRef.current?.play().catch(err => {
            dbg('[MediaEngine] Autoplay prevented:', err);
          });
        };
        activeRef.current.addEventListener('canplay', handleCanPlay);

        setTimeout(() => {
          if (activeRef.current && activeRef.current.paused && isPlaying) {
            activeRef.current.play().catch(err => {
              dbg('[MediaEngine] Fallback play prevented:', err);
            });
          }
        }, 1000);
      }
    } else {
      dbg('[MediaEngine] No media URL available:', currentTrack);
    }
  }, [currentTrack, isVideo, isPlaying, isAuthenticated, startPlaybackSession, stopPlaybackSession]);
  
  // ========== SYNC PLAY STATE ==========
  useEffect(() => {
    const ref = getActiveRef();
    if (!ref.current || !currentTrack) return;
    
    if (isPlaying) {
      ref.current.play().catch(err => {
        dbg('Play failed:', err);
        setIsPlaying(false);
      });
    } else {
      ref.current.pause();
    }
  }, [isPlaying, currentTrack, getActiveRef, setIsPlaying]);
  
  // ========== SYNC VOLUME ==========
  useEffect(() => {
    const effectiveVolume = isMuted ? 0 : volume;
    if (audioRef.current) audioRef.current.volume = effectiveVolume;
    if (videoRef.current) videoRef.current.volume = effectiveVolume;
  }, [volume, isMuted]);
  
  // ========== HANDLE SEEK COMMANDS ==========
  useEffect(() => {
    if (_seekTo === undefined) return;
    
    const ref = getActiveRef();
    if (ref.current) {
      ref.current.currentTime = _seekTo;
    }
    clearSeekCommand();
  }, [_seekTo, getActiveRef, clearSeekCommand]);
  
  // ========== EVENT HANDLERS ==========
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    trackEvent('play_start');
    
    // Record to history when playback starts (audio only - video records in VideoPlayer.jsx)
    if (currentTrack && !isVideo) {
      recordHistory(currentTrack.id);
    }
  }, [setIsPlaying, trackEvent, currentTrack, isVideo, recordHistory]);
  
  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, [setIsPlaying]);
  
  const handleTimeUpdate = useCallback((e) => {
    const time = e.target.currentTime;
    // Always use the actual media element duration for gate checks — it's available
    // immediately after metadata loads and is never stale like the store value can be
    // when startVideoTrack seeds it from a potentially wrong DB value.
    const mediaDuration = e.target.duration;
    const validDuration = mediaDuration > 0 && isFinite(mediaDuration);

    setCurrentTime(time);

    // Track analytics progress every 30 seconds
    if (time - lastTrackedTime.current >= 30) {
      lastTrackedTime.current = time;
      trackEvent('play_progress', {
        duration_seconds: 30,
        progress_percent: validDuration ? Math.round((time / mediaDuration) * 100) : 0,
      });
    }

    // ── Free-trial enforcement: stop at 80% and show paywall ──────────────────
    if (currentTrack && freeTrial?.episode_id === currentTrack.id && validDuration) {
      const pct = time / mediaDuration;

      if (pct >= 0.80 && !trialPausedRef.current) {
        trialPausedRef.current = true;
        const ref = getActiveRef();
        if (ref.current) ref.current.pause();
        setIsPlaying(false);
        setTrialExpiredPaywall(true);
        updateProgress(currentTrack.id, time, mediaDuration);
        return;
      }

      // Report progress every 15 seconds (only while not yet at the gate)
      const now = Date.now();
      if (now - lastProgressReportRef.current >= 15000) {
        lastProgressReportRef.current = now;
        updateProgress(currentTrack.id, time, mediaDuration);
      }
    }

  }, [setCurrentTime, trackEvent, currentTrack, freeTrial, updateProgress, getActiveRef, setIsPlaying, setTrialExpiredPaywall]);
  
  const handleLoadedMetadata = useCallback((e) => {
    const actualDuration = e.target.duration;
    setDuration(actualDuration);

    // Resume free-trial episode from saved position
    if (currentTrack && freeTrial?.episode_id === currentTrack.id) {
      const resumePos = currentTrack.free_trial_position || freeTrial.last_position_seconds || 0;
      if (resumePos > 5) {
        e.target.currentTime = resumePos;
      }
      lastProgressReportRef.current = Date.now();
    }

    // Auto-fix duration if it differs significantly from stored value
    if (currentTrack && actualDuration > 0 && accessToken) {
      const storedDuration = currentTrack.duration || 0;
      const diff = Math.abs(actualDuration - storedDuration);

      // If difference is more than 5 seconds, auto-fix
      if (diff > 5) {
        dbg(`[MediaEngine] Duration mismatch detected for "${currentTrack.title}": stored=${storedDuration}s, actual=${Math.round(actualDuration)}s`);
        fixDuration(currentTrack.id, Math.round(actualDuration));
      }
    }
  }, [setDuration, currentTrack, accessToken, freeTrial]);
  
  // Auto-fix duration in database when actual duration differs
  const fixDuration = useCallback(async (episodeId, actualDuration) => {
    try {
      await axios.post(
        `${API}/episodes/${episodeId}/fix-duration`,
        { client_duration: actualDuration },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      dbg(`[MediaEngine] Duration auto-fixed to ${actualDuration}s`);
    } catch (err) {
      console.error('[MediaEngine] Failed to fix duration:', err);
    }
  }, [accessToken]);
  
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    trackEvent('play_complete', {
      duration_seconds: Math.round(duration),
      progress_percent: 100,
    });
    // Report free-trial completion (100% = definitely consumed)
    if (currentTrack && freeTrial?.episode_id === currentTrack.id && duration > 0) {
      updateProgress(currentTrack.id, duration, duration);
    }
    // Video-code access: mark as consumed when video plays to the end, then show paywall.
    // The full video is always playable — the gate fires only when it naturally finishes.
    if (currentTrack?.is_code_access && accessToken) {
      axios.post(`${API}/video-codes/${currentTrack.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }).catch(() => {});
      setTrialExpiredPaywall(true);
      return; // Don't auto-play next — show paywall instead
    }
    // Auto-play next track from queue
    playNext();
  }, [setIsPlaying, trackEvent, duration, playNext, currentTrack, freeTrial, updateProgress, accessToken, setTrialExpiredPaywall]);
  
  // Handle media error
  const handleMediaError = useCallback((e) => {
    const error = e.target.error;
    console.error('[MediaEngine] Media error:', error);
    
    let errorMessage = 'Nepodařilo se přehrát médium';
    if (error) {
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMessage = 'Přehrávání bylo přerušeno';
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          errorMessage = 'Chyba sítě při načítání média';
          break;
        case MediaError.MEDIA_ERR_DECODE:
          errorMessage = 'Chyba dekódování média';
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = 'Formát média není podporován';
          break;
      }
    }
    setMediaError(errorMessage);
  }, [setMediaError]);

  // Both elements share the same event handlers
  const mediaProps = {
    onPlay: handlePlay,
    onPause: handlePause,
    onTimeUpdate: handleTimeUpdate,
    onLoadedMetadata: handleLoadedMetadata,
    onEnded: handleEnded,
    onError: handleMediaError,
  };
  
  // Elements are positioned off-screen but still functional.
  const hiddenStyle = {
    position: 'fixed',
    top: -9999,
    left: -9999,
    width: 320,
    height: 240,
    pointerEvents: 'none',
    zIndex: -1,
  };

  // When VideoPlayer is active it moves this element into its container div.
  // We switch to absolute fill so React's style reconciler stays in sync with
  // the DOM — no more conflict between hiddenStyle and our manual DOM styles.
  // Keys match hiddenStyle so React never resets an unexpected property to ''.
  const inPageStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 1,
    objectFit: 'contain',
    backgroundColor: '#000',
  };

  const videoStyle = isVideoInPage ? inPageStyle : hiddenStyle;
  
  return (
    <>
      {/* THE ONLY AUDIO ELEMENT IN THE ENTIRE APP */}
      <audio
        ref={audioRef}
        {...mediaProps}
        style={hiddenStyle}
        data-testid="media-engine-audio"
      />
      
      {/* THE ONLY VIDEO ELEMENT IN THE ENTIRE APP */}
      <video
        ref={videoRef}
        {...mediaProps}
        playsInline
        webkit-playsinline="true"
        preload="auto"
        muted={false}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        className="bg-black"
        style={videoStyle}
        data-testid="media-engine-video"
      />
    </>
  );
}

/**
 * Hook to get the video ref for displaying video in UI components.
 * The video element is owned by MediaEngine but can be "borrowed" for display.
 * This returns a stable ref that points to MediaEngine's video element.
 */
export function useVideoElement() {
  // We expose the video element through a store getter
  // This allows UI components to display the video visually
  return usePlayerStore.getState();
}
